import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaPlus, FaCheck, FaTimes, FaThumbsUp } from 'react-icons/fa';
import axios from 'axios';
import API_BASE_URL from '../../components/apiconfig/api-config';
import debounce from 'lodash/debounce';

const SanList = () => {
  const [sans, setSans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0 });
  const navigate = useNavigate();

  const [userRole, setUserRole] = useState('user');

  const fetchUserRole = async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/profile/profile-get`,
        { withCredentials: true }
      );
      if (response.data.success) {
        setUserRole(response.data.data.role);
      }
    } catch (error) {
      console.error('Error fetching user role:', error);
    }
  };

  const fetchSans = async (page = 1, search = '') => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_BASE_URL}/api/stock-adjustment/san-get?page=${page}&limit=${pagination.limit}&search=${search}`, { withCredentials: true });
      setSans(res.data.data);
      setPagination(prev => ({ ...prev, page, total: res.data.total }));
    } catch (err) {
      console.error('Error fetching SANs:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserRole();
    fetchSans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const debouncedSearch = debounce((search) => {
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchSans(1, search);
  }, 500);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    debouncedSearch(e.target.value);
  };

  const handleStatusUpdate = async (id, status) => {
    const confirmMessage = `Are you sure you want to ${status.toLowerCase()} this Stock Adjustment Note?`;
    if (!window.confirm(confirmMessage)) return;

    try {
      await axios.put(`${API_BASE_URL}/api/stock-adjustment/san-put-status/${id}`, { status }, { withCredentials: true });
      fetchSans(pagination.page, searchTerm);
    } catch (err) {
      console.error(`Error updating status to ${status}:`, err);
      alert(err.response?.data?.error || `Failed to update status to ${status}`);
    }
  };

  const renderActionButtons = (san) => {
    if (san.status === 'Pending' && (userRole === 'admin' || userRole === 'manager')) {
      return (
        <button
          onClick={() => handleStatusUpdate(san.san_id, 'Recommended')}
          className="flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-700 hover:bg-blue-200 rounded text-sm font-medium"
        >
          <FaThumbsUp /> Recommend
        </button>
      );
    }

    if (san.status === 'Recommended' && userRole === 'admin') {
      return (
        <div className="flex gap-2">
          <button
            onClick={() => handleStatusUpdate(san.san_id, 'Approved')}
            className="flex items-center gap-1 px-3 py-1 bg-green-100 text-green-700 hover:bg-green-200 rounded text-sm font-medium"
          >
            <FaCheck /> Approve
          </button>
          <button
            onClick={() => handleStatusUpdate(san.san_id, 'Rejected')}
            className="flex items-center gap-1 px-3 py-1 bg-red-100 text-red-700 hover:bg-red-200 rounded text-sm font-medium"
          >
            <FaTimes /> Reject
          </button>
        </div>
      );
    }

    return null;
  };

  return (
    <div className="max-w-8xl mx-auto p-2 bg-white rounded border border-gray-100">
      <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-2">
        <h2 className="text-xl font-semibold">Stock Adjustment Notes (SAN)</h2>
        <div className="flex gap-1 w-full sm:w-auto">
          <input
            type="text"
            placeholder="Quick Search..."
            value={searchTerm}
            onChange={handleSearch}
            className="border border-gray-200 rounded px-2 py-1 focus:outline-none w-full sm:w-48 text-sm"
          />
          <button
            className="flex items-center gap-1 bg-[#3899cf] text-white px-3 py-1 text-xs rounded hover:bg-[#0e8dc6] border border-[#10a5e9] font-medium"
            onClick={() => navigate('/san/new')}
          >
            <FaPlus /> Add New
          </button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-[#3899cf] text-white text-sm border-b">
              <th className="p-2 border font-medium">ID</th>
              <th className="p-2 border font-medium">Commodity</th>
              <th className="p-2 border font-medium">Vessel</th>
              <th className="p-2 border font-medium">Warehouse</th>
              <th className="p-2 border font-medium">Adjustment Type</th>
              <th className="p-2 border font-medium">Qty (MT)</th>
              <th className="p-2 border font-medium">Status</th>
              <th className="p-2 border font-medium text-center">Actions</th>
            </tr>
          </thead>
          <tbody>

            {loading ? (
              <tr><td colSpan="8" className="p-4 text-center text-sm text-gray-500">Loading...</td></tr>
            ) : sans.length === 0 ? (
              <tr><td colSpan="8" className="p-4 text-center text-sm text-gray-500">No records found.</td></tr>
            ) : (
              sans.map((san) => (
                <tr key={san.san_id} className="border-b hover:bg-gray-50 text-sm">
                  <td className="p-2 border text-gray-900">#{san.san_id}</td>
                  <td className="p-2 border text-gray-900">{san.commodity_name}</td>
                  <td className="p-2 border text-gray-600">{san.vessel_name || '-'}</td>
                  <td className="p-2 border text-gray-600">{san.warehouse_name}</td>
                  <td className="p-2 border">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      san.adjustment_type === 'Excess' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {san.adjustment_type} ({san.adjustment_type === 'Excess' ? '+' : '-'})
                    </span>
                  </td>
                  <td className="p-2 border text-gray-900">{san.quantity_mt}</td>
                  <td className="p-2 border">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      san.status === 'Approved' ? 'bg-green-100 text-green-800' :
                      san.status === 'Recommended' ? 'bg-blue-100 text-blue-800' :
                      san.status === 'Rejected' ? 'bg-red-100 text-red-800' :
                      'bg-yellow-100 text-yellow-800'
                    }`}>
                      {san.status}
                    </span>
                  </td>
                  <td className="p-2 border text-center">
                    <div className="flex justify-center">
                      {renderActionButtons(san)}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      
      {/* Pagination */}
      <div className="mt-4 flex flex-col sm:flex-row justify-between items-center text-sm text-gray-600 gap-2">
        <div>
          Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} results
        </div>
        <div className="flex gap-1">
          <button
            onClick={() => fetchSans(pagination.page - 1, searchTerm)}
            disabled={pagination.page === 1}
            className="px-3 py-1 border rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed text-xs"
          >
            Previous
          </button>
          <button
            onClick={() => fetchSans(pagination.page + 1, searchTerm)}
            disabled={pagination.page * pagination.limit >= pagination.total}
            className="px-3 py-1 border rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed text-xs"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default SanList;
